(function frameCartMergeBundle(scope) {
  'use strict';
  if (scope.FleuryFrameCartMerge) {
    if (typeof module !== 'undefined') module.exports = scope.FleuryFrameCartMerge;
    return;
  }

  const SOURCE = 'fa-frame-cart-merge';
  const LEGACY_KEYS = ['_konfigurator', '_leinwandformat', '_normalisiertes_format', '_sku'];

  function frameDimensions(sku) {
    const match = /^SF\+(\d{5,6})(?:\s|$)/.exec(String(sku || ''));
    if (!match) return null;
    const digits = match[1];
    const width = Number(digits.slice(0, -3));
    const height = Number(digits.slice(-3));
    return width > 0 && height > 0 ? [width, height].sort((a, b) => a - b) : null;
  }

  function lineKind(item) {
    if (!item || typeof item !== 'object') return null;
    const dimensions = frameDimensions(item.sku);
    if (!dimensions || typeof item.key !== 'string' || !item.key || !/^\d+$/.test(String(item.variant_id || ''))
      || !Number.isSafeInteger(item.original_price) || item.original_price < 0
      || !Number.isSafeInteger(item.final_price) || item.final_price < 0
      || !Number.isSafeInteger(item.quantity) || item.quantity < 1
      || item.selling_plan_allocation || item.selling_plan || item.gift_card
      || item.parent_relationship || item.has_components || item.item_components?.length
      || item.instructions?.can_remove === false || item.instructions?.can_update_quantity === false
      || item.line_level_discount_allocations?.length || item.discounts?.length) return null;

    const properties = item.properties || {};
    if (typeof properties !== 'object' || Array.isArray(properties)) return null;
    const keys = Object.keys(properties);
    if (!keys.length) return 'clean';
    if (properties._konfigurator !== 'schattenfugenrahmen' || keys.some(key => !LEGACY_KEYS.includes(key))) return null;
    if (properties._sku !== undefined && properties._sku !== item.sku) return null;
    for (const name of ['_leinwandformat', '_normalisiertes_format']) {
      if (properties[name] === undefined) continue;
      const match = /^(\d+)x(\d+) cm$/.exec(String(properties[name]));
      if (!match) return null;
      const selected = [Number(match[1]), Number(match[2])].sort((a, b) => a - b);
      if (selected[0] !== dimensions[0] || selected[1] !== dimensions[1]) return null;
    }
    return 'legacy';
  }

  // Only this configurator's obsolete metadata is ignored. Other configuration,
  // price, subscription and bundle differences must retain distinct Shopify lines.
  function buildMergeUpdates(cart) {
    const groups = new Map();
    for (const item of Array.isArray(cart?.items) ? cart.items : []) {
      const kind = lineKind(item);
      if (!kind) continue;
      const identity = JSON.stringify([String(item.variant_id), item.sku, item.original_price, item.final_price]);
      if (!groups.has(identity)) groups.set(identity, []);
      groups.get(identity).push({ item, kind });
    }
    const updates = {};
    for (const group of groups.values()) {
      if (group.length < 2 || !group.some(line => line.kind === 'legacy')) continue;
      const quantity = group.reduce((sum, line) => sum + line.item.quantity, 0);
      if (!Number.isSafeInteger(quantity)) continue;
      if (new Set(group.map(line => line.item.key)).size !== group.length) continue;
      const keeper = group.find(line => line.kind === 'clean') || group[0];
      for (const line of group) updates[line.item.key] = line === keeper ? quantity : 0;
    }
    return Object.keys(updates).length ? updates : null;
  }

  function cartRoot() {
    const root = scope.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  }

  function hasLegacyLines(cart) {
    return cart?.items?.some(item => item.properties?._konfigurator === 'schattenfugenrahmen');
  }

  function lockControls() {
    const nodes = [...scope.document.querySelectorAll('cart-items-component, .cart-page__summary, .cart__ctas, [data-add-frame], [data-cross-selling-add]')];
    const states = nodes.map(node => [node, node.inert]);
    for (const [node] of states) node.inert = true;
    return () => { for (const [node, inert] of states) node.inert = inert; };
  }

  async function readCart() {
    const response = await scope.fetch(`${cartRoot()}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (!response.ok) throw new Error(`Frame cart read failed (${response.status})`);
    return response.json();
  }

  let pending = null;
  let timer = null;
  let revision = 0;
  async function mergeFreshCart(options) {
    if (scope.document.querySelector('.cart-items-disabled')) return null;
    const unlock = lockControls();
    try {
      const startRevision = revision;
      const cart = await readCart();
      if (revision !== startRevision) { schedule(); return null; }
      const updates = buildMergeUpdates(cart);
      if (!updates) return cart;
      const sectionIds = [...new Set([...scope.document.querySelectorAll('cart-items-component[data-section-id]')]
        .map(node => node.dataset.sectionId).filter(Boolean))].slice(0, 5);
      const response = await scope.fetch(`${cartRoot()}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates, sections: sectionIds, sections_url: scope.location.pathname })
      });
      if (!response.ok) throw new Error(`Frame cart merge failed (${response.status})`);
      const merged = await response.json();
      if (merged.errors || !Array.isArray(merged.items)) throw new Error('Frame cart merge returned no cart');
      // This operation is a redistribution of existing quantities, never a new purchase.
      if (merged.item_count !== cart.item_count) throw new Error('Cart changed during frame merge; no automatic retry');
      if (options.publish !== false) {
        scope.document.dispatchEvent(new scope.CustomEvent('cart:update', {
          bubbles: true,
          detail: { resource: merged, sourceId: SOURCE, data: { source: SOURCE, itemCount: merged.item_count, sections: merged.sections } }
        }));
      }
      return merged;
    } finally {
      unlock();
    }
  }

  function reconcile(options = {}) {
    if (pending) return pending;
    const run = () => mergeFreshCart(options);
    pending = (scope.navigator?.locks?.request
      ? scope.navigator.locks.request(SOURCE, run)
      : run()).finally(() => { pending = null; });
    return pending;
  }

  function schedule() {
    scope.clearTimeout(timer);
    timer = scope.setTimeout(() => {
      if (scope.document.querySelector('.cart-items-disabled')) { schedule(); return; }
      reconcile().catch(error => scope.console?.warn('[FLEURY frame cart]', error.message));
    }, 100);
  }

  const api = { buildMergeUpdates, reconcile };
  scope.FleuryFrameCartMerge = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (!scope.document) return;

  const start = () => {
    if (scope.document.querySelector('[data-fa-frame-legacy-line]')) schedule();
  };
  if (scope.document.readyState === 'loading') scope.document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  scope.addEventListener('pageshow', event => { if (event.persisted) start(); });
  scope.document.addEventListener('cart:update', event => {
    if (event.detail?.data?.source === SOURCE) return;
    revision++;
    if (hasLegacyLines(event.detail?.resource) || scope.document.querySelector('[data-fa-frame-legacy-line]')) schedule();
  });
}(typeof window !== 'undefined' ? window : globalThis));
